<template>
  <Stack direction="col" align="end" gap="xs" class="relative md:px-sm" v-on-click-outside="closeDropdown">
    <Stack
      :id="`actions-${props.document.glitem}`"
      align="center"
      justify="center"
      class="p-2xs border-sm md:border-none border-mkm-blue-light rounded-sm cursor-pointer"
      @click="toggleDropdown"
      data-test-id="dotsBtn"
    >
      <Icon name="elipses" :size="14" :color="getColor('mkm-blue-light')" />
    </Stack>

    <Teleport to="body">
      <Dropdown
        v-if="isExpanded"
        :active="isExpanded"
        class="!w-[140px] absolute z-50 !shadow-all-sm mt-xl"
        :style="dropdownStyles"
        data-test-id="invoiceActionsDropdown"
      >
        <aside class="w-full bg-white py-2xs">
          <nav>
            <ul>
              <li class="h-full w-full bg-white hover:bg-grey-default" @click="toggleDropdown">
                <Stack tag="button" class="block w-full h-full px-sm py-[12px]" @click="handleDownload">
                  <Text tag="span" weight="semi" align="left" data-test-id="downloadSingleInvoiceBtn">Download</Text>
                </Stack>
              </li>
              <li v-if="canPay" class="h-full w-full bg-white hover:bg-grey-default" @click="toggleDropdown">
                <Stack tag="button" class="block w-full h-full px-sm py-[12px]" @click="handlePay">
                  <Text tag="span" weight="semi" align="left" data-test-id="paySingleInvoiceBtn">Pay</Text>
                </Stack>
              </li>
              <li class="h-full w-full bg-white hover:bg-grey-default" @click="toggleDropdown">
                <Stack tag="button" class="block w-full h-full px-sm py-[12px]" @click="handleView">
                  <Text tag="span" weight="semi" align="left" data-test-id="viewSingleInvoiceBtn">View</Text>
                </Stack>
              </li>
            </ul>
          </nav>
        </aside>
      </Dropdown>
    </Teleport>
  </Stack>
</template>

<script lang="ts" setup>
import { FormattedDocument } from "~/composables/useDocuments/useDocumentsTypes";
import { InvoiceActionsMenuProps } from "./invoiceActionsMenuTypes";
import { getColor } from "mkm-avengers";
import { vOnClickOutside } from "@vueuse/components";

const props = defineProps<InvoiceActionsMenuProps>();

const { BillableStatus } = useDocuments();

const emit = defineEmits<{
  (event: "download", document: FormattedDocument): void;
  (event: "pay", document: FormattedDocument): void;
  (event: "view", document: FormattedDocument): void;
}>();

const canPay = computed(
  () => props.document.document_type === "Invoice" && BillableStatus.includes(props.document.status),
);

const isExpanded = ref<boolean>(false);

const dropdownStyles = ref({
  left: "0px",
  top: "0px",
});

const toggleDropdown = () => {
  isExpanded.value = !isExpanded.value;

  if (isExpanded.value) {
    const buttonElement = document.querySelector(`#actions-${props.document.glitem}`);
    const buttonRect = buttonElement?.getBoundingClientRect();

    if (buttonRect) {
      const dropdownMaxHeight = canPay.value ? 160 : 112;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow >= dropdownMaxHeight) {
        // Enough space below the button, position the dropdown below
        dropdownStyles.value.top = `${buttonRect.bottom - 45 + window.scrollY}px`;
      } else if (spaceAbove >= dropdownMaxHeight) {
        // Enough space above the button, position the dropdown above
        dropdownStyles.value.top = `${buttonRect.top - dropdownMaxHeight - 50 + window.scrollY}px`;
      } else {
        // If neither above nor below has enough space, prioritize below
        dropdownStyles.value.top = `${buttonRect.bottom - 45 + window.scrollY}px`;
      }

      dropdownStyles.value.left = `${buttonRect.right - 140 + window.scrollX}px`;
    }
  }
};

const closeDropdown = () => (isExpanded.value = false);

const handleDownload = (): void => emit("download", props.document);

const handlePay = (): void => emit("pay", props.document);

const handleView = (): void => emit("view", props.document);
</script>
